import { Component, Input, inject } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Language } from '@ubie/core';
import { LanguageBoxAppearance } from './language-box-appearance.type';

@Component({
  selector: 'shared-languagebox-v2',
  styleUrls: ['./languagebox-v2.component.scss'],
  templateUrl: './languagebox-v2.component.html',
  standalone: false,
})
/**
 * Dropdown to select a new language
 * --------
 * FLOW
 * --------
 * Get languages and display them
 * Reload language data when dropdown value changed
 */
export class LanguageboxV2Component {
  private readonly formBuilder = inject(UntypedFormBuilder);
  private readonly translateService = inject(TranslateService);

  /////////////////////////////////////////////////////////
  //#region PROPERTIES
  /////////////////////////////////////////////////////////

  // form formgroup
  form?: UntypedFormGroup;

  // When force reload is set to true, the dropdown data will be reloaded/updated
  @Input()
  set forceReload(value: boolean) {
    if (value) {
      this.initForm();
    }
  }

  // language box type
  @Input() languageBoxType: LanguageBoxAppearance = 'select-box';

  // Set translation type (public/client/admin)
  @Input() translationType?: string;
  // Default language
  @Input() defaultLanguageId?: number;
  // Received languages
  _languages: Language[] = [];
  @Input()
  set languages(value: Language[]) {
    this._languages = value;
    // init form
    this.initForm();
  }
  get languages(): Language[] {
    return this._languages;
  }

  /**
   * Init the form
   */
  private initForm(): void {
    if (this.languages !== undefined) {
      // is only initiated once
      if (this.form === undefined) {
        this.form = this.formBuilder.group({
          language: [this.defaultLanguageId, [Validators.required]],
        });
      }

      this.initFormData();
    }
  }

  /**
   * Set data for the form
   */
  private initFormData(): void {
    const selectedLanguage = this.getCurrentLanguage();

    // set form values
    this.form?.setValue(
      {
        language: selectedLanguage === undefined ? '' : selectedLanguage,
      },
      {
        emitEvent: false,
      },
    );

    // add onchanges handler
    this.onChanges();
  }

  /***
   * Get currently used (or a default) language
   */
  private getCurrentLanguage(): number {
    // find active language
    let currentLang = 1; // set 1 as default

    // if a currentlang was set in the translate service; language should be formatted as e.g.: 'client_1', 'public_3'
    if (
      this.translateService.currentLang !== undefined &&
      this.translateService.currentLang !== null &&
      this.translateService.currentLang.split('_').length === 2
    ) {
      const cLang = Number(this.translateService.currentLang.split('_')[1]);
      if (!isNaN(cLang)) {
        currentLang = cLang;
      }
    } else {
      // if the defaultlang was set in the translate service; language should be formatted as e.g.: 'client_1', 'public_3'
      if (
        this.translateService.defaultLang !== undefined &&
        this.translateService.defaultLang !== null &&
        this.translateService.defaultLang.split('_').length === 2
      ) {
        const cLang = Number(this.translateService.defaultLang.split('_')[1]);
        if (!isNaN(cLang)) {
          currentLang = cLang;
        }
      }
    }

    return currentLang;
  }

  /**
   * Form values change
   */
  onChanges(): void {
    // update when dropdown was changed
    this.form?.valueChanges.subscribe((val) => {
      this.updateObject();
    });
  }

  /**
   * Update user
   */
  updateObject(): void {
    if (this.form?.valid) {
      // set a default translation type in case it was not supplied
      this.updateTranslation(this.form.value['language']);
    }
  }

  updateTranslation(language: any): void {
    const type =
      this.translationType === null || this.translationType === undefined
        ? 'public'
        : this.translationType;
    this.translateService.use(type + '_' + language);
    this.defaultLanguageId = language;
  }

  // change current language
  changeLanguage(selectedLanguage: any): void {
    this.updateTranslation(selectedLanguage);
  }
}
